import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchPlantsByName } from '../../../api/api_plants';
import { fetchCustomPlantsByName } from '../../../api/api_customPlants';
import { updatePot } from '../../../api/api_pots';
import { fetchUsernameByID } from '../../../api/api_users';

const AddPlant = () => {
  const { translations } = useLanguage();
  const location = useLocation();
  const [selectedPot, setSelectedPot] = useState(() => {
    return location.state?.pot || JSON.parse(localStorage.getItem('selectedPot')) || '';
  });
  const [selectedPlantType, setSelectedPlantType] = useState('Usual Plant');
  const [plantName, setPlantName] = useState('');
  const [plantList, setPlantList] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedPot) {
      localStorage.setItem('selectedPot', JSON.stringify(selectedPot));
    }
  }, [selectedPot]);

  useEffect(() => {
    if (!selectedPot) {
      navigate("/pots/potManager");
    }
  }, [selectedPot, navigate]);

  useEffect(() => {
    if (location.state && location.state.scannedPlantName) {
      setPlantName(location.state.scannedPlantName);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchPlants = useCallback(async (selectedType, name) => {
    try {
      let plantsData;
      if (selectedType === 'Custom Plant') {
        plantsData = await fetchCustomPlantsByName(name);
      } else {
        plantsData = await fetchPlantsByName(name);
      }
      if (Array.isArray(plantsData.data) && plantsData.data.length > 0) {
        const updatedPlants = await Promise.all(plantsData.data.map(async plant => {
          if (plant.user_id) {
            try {
              const username = await fetchUsernameByID(plant.user_id);
              return { ...plant, name: `${username.data} - ${plant.name}` };
            } catch (error) {
              return plant;
            }
          } else {
            return plant;
          }
        }));
        setPlantList(updatedPlants);
        setError('');
      } else {
        setPlantList([]);
        setError('No matching plants found.');
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setPlantList([]);
        setError('No matching plants found.');
      } else {
        setError(translations.error_fetching_plants);
      }
    }
  }, [translations.error_fetching_plants]);

  useEffect(() => {
    if (plantName.length > 2) {
      fetchPlants(selectedPlantType, plantName);
    } else {
      setPlantList([]);
      setError('');
    }
  }, [plantName, selectedPlantType, fetchPlants]);

  const handleBack = () => navigate(-1);
  const handleScanPlant = () => navigate("scanPlant", { state: { pot: selectedPot } });
  const handleCreateCustomPlant = () => navigate("/customPlants/create");

  const handleAddPlantToPot = (plantID, plantName) => {
    const token = localStorage.getItem('userToken');
    const confirmMessage = translations.confirm_add_plant.replace('{plantName}', plantName);
    if (window.confirm(confirmMessage)) {
      const updatedPot = { ...selectedPot };
      if (selectedPlantType === 'Custom Plant') {
        updatedPot.custom_plant_ids = updatedPot.custom_plant_ids
          ? `${updatedPot.custom_plant_ids},${plantID}`
          : `${plantID}`;
      } else {
        updatedPot.plant_ids = updatedPot.plant_ids
          ? `${updatedPot.plant_ids},${plantID}`
          : `${plantID}`;
      }
      updatePot(updatedPot.id, updatedPot, false, token).then(() => {
        handleBack();
      });
    }
  };

  return (
    <div className="plant-page">
      <div className="sm-container">
        <div className="header">
          <button onClick={handleBack} className="back-button">&#8592;</button>
          <h1>{`${translations.add_plant_to_pot} ${selectedPot.name}`}</h1>
        </div>
        <select
          className="general-input pot-select"
          value={selectedPlantType}
          onChange={(e) => setSelectedPlantType(e.target.value)}
        >
          <option value="Usual Plant">{translations.normal_plant}</option>
          <option value="Custom Plant">{translations.custom_plant}</option>
        </select>
        <div className="content">
          <label htmlFor="name">{translations.plant_name}:</label>
          <input
            id="name"
            type="text"
            className="general-input"
            value={plantName}
            onChange={(e) => setPlantName(e.target.value)}
          />
        </div>
        <div className="sm-container">
          <label htmlFor="name">{translations.fetched_plants}:</label>
          {error && <p className="general-error">{error}</p>}
          <ul className="plant-ul">
            {plantList.length > 0 ? plantList.map((plant, index) => (
              <button
                key={index}
                className="general-button"
                onClick={() => handleAddPlantToPot(plant.id, plant.name)}
              >
                {plant.name}
              </button>
            )) : null}
          </ul>
          <div className="content">
            {selectedPlantType === 'Usual Plant' ? (
              <div className="content">
                <button onClick={handleScanPlant} className="general-button fastSettings-button">{translations.scann_plant}</button>
              </div>
            ) : (
              <button onClick={handleCreateCustomPlant} className="general-button secondary-button">{translations.create_custom_plant}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPlant;
